import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import axios from 'axios';
import { getParams } from '../../common/AuraFunctions';
import { excludeCustomerOrderWaitingApprovalStatuses, scopeCustomerWaitingApprovalStatuses } from './customerWaitingApprovalStatuses';
import logger from 'app/utils/logger';

export const getOrders = createAsyncThunk(
  'eCommerceApp/orders/getOrders',
  // PGSS-5451: `= {}` because OrderDialog dispatches this with no argument at all. Destructuring
  // undefined threw, so that call rejected on the spot -- but `pending` had already cleared the
  // table, which is why the list went blank after saving instead of reloading. It also has to be
  // fixed HERE for the guard below to be safe: a request that registers as current and then dies
  // would make a real in-flight response look stale and get dropped.
  async ({ params = {}, goToFirstPage } = {}, { dispatch, getState, requestId }) => {
    dispatch(setLoading(true))
    if (params.stage === 'completed') params.stage = 'submitted'
    const { filterObj, headerObj } = getParams(goToFirstPage)
    const headers = { 'App-Pagination-Limit': 10, 'App-Pagination-Num': 0, ...headerObj };
    const response = await axios.get('/api/orders', { params: { ...params, ...filterObj }, headers });
    const data = await response.data.data || response.data?.supplier_invoice;

    // PGSS-5448: only the newest request may publish. Search, filter and paging all fire a fresh
    // request without cancelling the last one, so a slow earlier response can land after a fast
    // later one. The reducers below drop the stale rows; these three dispatches are plain actions
    // that would sail straight past that guard and leave the pagination header, the stored params
    // and the loading flag describing a request whose rows are not on screen.
    if (getState()?.eCommerceApp?.orders?.ordersRequestId !== requestId) return data;

    dispatch(setOrderListHeaders(response.headers))
    dispatch(setParams(params))
    dispatch(resetLoading(false))
    return data;
  }
);

export const removeOrders = createAsyncThunk(
  'eCommerceApp/orders/removeOrders',
  async (orderIds, { dispatch, getState }) => {
    await axios.post('/api/e-commerce-app/remove-orders', { orderIds });

    return orderIds;
  }
);

export const deleteOrders = createAsyncThunk(
  'eCommerceApp/orders/deleteOrders',
  async (payload, { dispatch, getState }) => {
    const response = await axios.delete('/api/order', { data: payload });
    const data = await response.data
    return data;
  }
);

export const deleteShipment = createAsyncThunk(
  'eCommerceApp/orders/deleteShipment',
  async (shipmentId, { dispatch, getState }) => {
    const response = await axios.put(`/api/shipment/${shipmentId}`, {}, { params: { delete_shipment: true } });
    const data = await response.data
    return data;
  }
);

export const uploadOrders = createAsyncThunk(
  'eCommerceApp/orders/uploadOrders',
  async (params, { dispatch, getState }) => {
    const formData = new FormData();
    let url = ''
    if (params.single)
      url = '/api/importOrder'
    else
      url = '/api/importOrders'
    delete params.single

    Object.keys(params).forEach((key) => {
      formData.append(key, params[key]);
    });
    let response
    try {
      response = await axios
        .post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
    } catch (error) {
      logger.error('Order import upload failed', error);
      // PGSS-5795: this returned {}, which the import dialog rendered as "All ... are uploaded
      // successfully", so a rejected upload looked like a queued one and then nothing ever came.
      const serverMessage = error?.response?.data?.message;
      return {
        is_error: true,
        message: serverMessage || 'The file could not be uploaded, so nothing was imported. Please try again.',
      };
    }

    return response?.data || {};
  }
);

export const getOrderStatuses = createAsyncThunk(
  'eCommerceApp/orders/getOrderStatuses',
  async ({ params }) => {
    const response = await axios.get('/api/categoryValues', { params });
    const data = await response.data;
    return { data, params };
  }
);

export const getUpdateStatusValues = createAsyncThunk(
  'eCommerceApp/orders/getUpdateStatusValues',
  async (params) => {
    const response = await axios.get('/api/updateStatusesValues', { params });
    return response.data;
  }
);

export const sendInvoice = createAsyncThunk(
  'eCommerceApp/orders/sendInvoice',
  async (inputData, { dispatch, getState }) => {
    dispatch(setLoading())
    const response = await axios.post('/api/sendInvoice', inputData);
    const data = await response.data;
    dispatch(resetLoading())
    return data;
  }
);

export const getSelectedOrderDetails = createAsyncThunk(
  'eCommerceApp/orders/getSelectedOrderDetails',
  async (oid, { dispatch, getState }) => {
    const response = await axios.get(`/api/orders/${oid}`);
    const data = [
      {
        orders: [{ ...response.data.order, order_detail: response.data.order.order_details }],
      },
    ];
    dispatch(selectedOrderDetails(data));
    return data;
  }
);

export const getSuppliers = createAsyncThunk(
  'eCommerceApp/orders/viewSupplier',
  async (suppliers, { dispatch }) => {
    const response = await axios.get(`/api/suppliers`);
    const data = await response.data.data;
    dispatch(setPreviousSuppliers(suppliers || []));
    return data;
  }
);

export const getOrderForReallocation = createAsyncThunk(
  'eCommerceApp/orders/getOrderForReallocation',
  async (id, { dispatch }) => {
    const response = await axios.get(`/api/getOrderForReallocation?oid=${id}`);
    const data = await response.data.availableOrders;
    dispatch(orderForReallocation([data]));
    return [data];
  }
);

export const createReallocation = createAsyncThunk(
  'eCommerceApp/orders/createReallocation',
  async (inputData, { dispatch }) => {
    const response = await axios.put('api/reallocateOrder', {
      reallocate_order: [inputData],
    });
    const data = await response.data;
    return data;
  }
);

export const viewSupplierShipment = createAsyncThunk(
  'eCommerceApp/orders/viewSupplierShipment',
  async (id, { dispatch }) => {
    const response = await axios.get(`api/viewSupplierShipment/${id}`);
    const data = await response.data[0];
    return { ...data, eta_date: data.eta_date?.split('T')[0] };
  }
);

export const getPaymentHistory = createAsyncThunk(
  'DashboardApp/widgets/getPaymentHistory',
  async (id) => {
    const response = await axios.get(`/api/payment/${id}`);
    const data = await response.data.paymentDetails;
    return data
  }
);

/**
 * PGSS-5326 — reverse a payment that came back. `mode` is MONEY_RETURNED when real money was
 * received and returned, or NEVER_RECEIVED for a keying error or duplicate that was never real.
 */
export const reversePayment = createAsyncThunk(
  'DashboardApp/widgets/reversePayment',
  async ({ invoiceId, paymentId, mode, reason, source }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/invoice/${invoiceId}/payment/${paymentId}/reverse`,
        // PGSS-5557: `source` tells the server which table the id belongs to. Omitted means an
        // invoice payment, which is what every caller sent before advance payments became
        // reversible.
        { mode, reason, source }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || '');
    }
  }
);

export const getContainerNumber = createAsyncThunk(
  'DashboardApp/widgets/getContainerNumber',
  async (params) => {
    const response = await axios.get(`/api/containerNumber`, { params });
    const data = await response.data;
    return data
  }
);

/** Bulk order imports */
export const getImportOrders = createAsyncThunk(
  'eCommerceApp/orders/getImportOrders',
  async (date, { dispatch }) => {
    dispatch(setLoading(true))
    const formattedDate = moment(date).format('MM-DD-YYYY')
    const response = await axios.get(`/api/bulkImport/${formattedDate}`);
    const data = await response.data;
    return { data, date };
  }
);

/** Single order imports */
export const getSingleImportOrders = createAsyncThunk(
  'eCommerceApp/orders/getSingleImportOrders',
  async (date, { dispatch }) => {
    dispatch(setLoading(true))
    const formattedDate = moment(date).format('MM-DD-YYYY')
    const response = await axios.get(`/api/singleImport/${formattedDate}`);
    const data = await response.data;
    return { data, date };
  }
);

export const getImportOrdersDetail = createAsyncThunk(
  'eCommerceApp/orders/getImportOrdersDetail',
  async (id, { dispatch }) => {
    dispatch(setLoading(true))
    const response = await axios.get(`/api/bulkImportData/${id}`);
    const data = await response.data;
    return data;
  }
);

/** Product imports of one type for a day: price_book (default), active or archive */
export const getPriceBookImports = createAsyncThunk(
  'eCommerceApp/orders/getPriceBookImports',
  async ({ date, importType = 'price_book' }, { dispatch }) => {
    dispatch(setLoading(true))
    const formattedDate = moment(date).format('MM-DD-YYYY')
    const response = await axios.get('/api/importProducts/view', { params: { type: importType, date: formattedDate } });
    const data = await response.data;
    return { data, date, importType };
  }
);

export const getCustomersList = createAsyncThunk(
  'eCommerceApp/orders/getCustomersList',
  async (routeParams, { getState }) => {
    // Created Seperate function for customer list drpodown values
    routeParams = routeParams || getState().customersApp.customers.routeParams;
    const response = await axios.get('/api/customers', {
      params: routeParams,
    });
    const data = await response.data;
    return { data, routeParams };
  }
);

export const getStatuses = createAsyncThunk(
  'eCommerceApp/orders/getStatuses',
  async (params) => {
    const response = await axios.get('/api/categoryValues', { params: { c: params } });
    const resData = await response.data;
    const data = resData?.filter(r => r.name !== 'Inventory Received')
    return { data, params };
  }
);

export const getShipToLocations = createAsyncThunk(
  'eCommerceApp/orders/getShipToLocations',
  async (id) => {
    const response = await axios.get(`/api/customerProfile/${id}`);
    const resData = await response.data?.profile;
    return resData || [];
  }
);

export const getRequestedByList = createAsyncThunk(
  'eCommerceApp/orders/getRequestedByList',
  async (id, { getState }) => {
    const response = await axios.get(`/api/customerUsers/${id}`);
    const data = await response.data?.customer_users;
    return data || [];
  }
);

export const getStoneOrder = createAsyncThunk(
  'eCommerceApp/orders/getStoneOrder',
  async (id, { dispatch }) => {
    const response = await axios.get(`/api/getStoneOrder?order_id=${id}`);
    const data = await response.data?.order;
    return data;
  }
);

export const exportOrders = createAsyncThunk(
  'eCommerceApp/orders/exportOrders',
  async ({ payload, params }, { dispatch, getState }) => {
    const response = await axios.post('/api/exportOrders', payload, { params, responseType: 'blob',});
    return response;
  }
);

// changed the logic to handle shipped orders and inproduction orders case (only case where both id and shipment_id comes)
const ordersAdapter = createEntityAdapter({
  selectId: (entity) => {
    if (entity.id && entity.shipment_id) {
      // both exist prefers id
      return entity.id;
    }
    if (entity.id) {
      // only id exists then takes it 
      return entity.id;
    }
    if (entity.shipment_id) {
      // only shipment_id exists then shipment id
      return entity.shipment_id;
    }
    return entity.id;
  }
});

// Thunk to fetch products for reordering based on order IDs
export const reorderProducts = createAsyncThunk(
  'eCommerceApp/orders/reorderProducts',
  async (orderIds) => {
    const response = await axios.post('/api/getReorderProducts', {order_ids : orderIds});
    return response;
  }
);

export const getContainerNumbersList = createAsyncThunk(
  'eCommerceApp/orders/getContainerNumbersList',
  async () => {
    const response = await axios.get(`/api/containerNumbers`);
    const data = await response.data?.data;
    return data || [];
  }
);

export const getOrderList = createAsyncThunk(
  'eCommerceApp/orders/getOrderList',
  async (orderNo) => {
    const response = await axios.get(`/api/ordersList?orderNo=${orderNo}`);
    return response;
  }
);

// This api is used to get the order items and status information in one API
export const getOrderPreview = createAsyncThunk(
  "eCommerceApp/orders/getOrderPreview",
  async (params) => {
    const response = await axios.post(`/api/preview`, null, { params });
    return response.data;
  }
);

// Legacy smart status update (workflow/VAS/shipment side effects)
export const updateOrderForStatus = createAsyncThunk(
  'eCommerceApp/orders/updateOrderForStatus',
  async ({ payload }, { dispatch, getState }) => {
    const response = await axios.put('/api/updateOrder', payload);
    return response?.data;
  }
);

// Direct dumb status update for Order Status Fix page (console-style)
export const applyAdminOrderStatus = createAsyncThunk(
  'eCommerceApp/orders/applyAdminOrderStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/admin/order-status/apply', payload);
      return response?.data;
    } catch (error) {
      const data = error?.response?.data;
      const message =
        (typeof data === 'string' && data) ||
        data?.message ||
        'Error processing order';
      return rejectWithValue(message);
    }
  }
);

export const { selectAll: selectOrders, selectById: selectOrderById } = ordersAdapter.getSelectors(
  (state) => state.eCommerceApp.orders
);

const reallocationState = {
  reAllocationDialog: {
    props: {
      open: false,
    },
    data: null,
  },
  reAllocationFrom: null,
  reAllocationTo: null,
  selectedOrderDetails: [],
  orderForReallocation: [],
  paymentHistory: [],
};

export const initState = {
  loading: false,
  ordersRequestId: null,
  searchText: '',
  customer: null,
  supplier: null,
  orderStatusesNames: [],
  paymentTypeNames: [],
  shipToNames: [],
  requestedByNames: [],
  orderTypeNames: [],
  orderStatuses: [],
  suppliersList: [],
  previousSuppliers: [],
  suppliersListLoading: false,
  supplierShipmentValues: {},
  orderListHeaders: {},
  selectedValue: null,
  params: {},
  orderTypes: [],
  importOrders: [],
  singleImportOrders: [],
  priceBookImports: [],
  importDetails: [],
  ...reallocationState,
  customersListItems: [],
  orderTypeListItems: [],
  salesPersonListItems: [],
  loadStatusesNames: [],
  containerShipmentNames:[],
  containerNumbers:[],
  resetFilter: false
};

const ordersSlice = createSlice({
  name: 'eCommerceApp/orders',
  initialState: ordersAdapter.getInitialState({
    loading: false,
    ordersRequestId: null,
    searchText: '',
    customer: null,
    salesPerson: null,
    supplier: null,
    orderStatusesNames: [],
    orderTypeNames: [],
    shipmentTypeNames: [],
    paymentTypeNames: [],
    shipToNames: [],
    requestedByNames: [],
    orderStatuses: [],
    suppliersList: [],
    previousSuppliers: [],
    suppliersListLoading: false,
    supplierShipmentValues: {},
    orderListHeaders: {},
    selectedValue: null,
    params: {},
    orderTypes: [],
    importOrders: [],
    singleImportOrders: [],
    priceBookImports: [],
    importDetails: [],
    ...reallocationState,
    customersListItems: [],
    orderTypeListItems: [],
    salesPersonListItems: [],
    loadStatusesNames: [],
    containerShipmentNames: [],
    containerNumbers:[],
  }),
  reducers: {
    setOrdersSearchText: {
      reducer: (state, action) => {
        state.searchText = action.payload;
      },
      prepare: (event) => ({ payload: event.target.value || '' }),
    },
    setLoading: (state, action) => {
      state.loading = true
    },
    resetLoading: (state, action) => {
      state.loading = false
    },
    setCustomer: (state, action) => {
      state.customer = action.payload || null;
    },
    setSalesPerson: (state, action) => {
      state.salesPerson = action.payload || null
    },
    setSupplier: (state, action) => {
      state.supplier = action.payload || null;
    },
    setSelectedValue: (state, action) => {
      state.selectedValue = action.payload || null
    },
    openReallocationDialog: (state, action) => {
      state.reAllocationDialog = {
        props: {
          open: true,
        },
        param: action.payload.param,
      };
    },
    closeReallocationDialog: (state) => {
      return { ...state, ...reallocationState };
    },
    reAllocationFrom: (state, action) => {
      state.reAllocationFrom = action.payload;
    },
    reAllocationTo: (state, action) => {
      state.reAllocationTo = action.payload;
    },
    selectedOrderDetails: (state, action) => {
      state.selectedOrderDetails = action.payload;
    },
    orderForReallocation: (state, action) => {
      state.orderForReallocation = action.payload;
    },
    setPreviousSuppliers: (state, action) => {
      state.previousSuppliers = action.payload;
    },
    setOrderListHeaders: (state, action) => {
      state.orderListHeaders = action.payload;
    },
    setParams: (state, action) => {
      state.params = action.payload
    },
    setOrderTypes: (state, action) => {
      state.orderTypes = action.payload
    },
    setCustomersListItems: (state, action) => {
      state.customersListItems = action.payload
    },
    setResetFilters: (state, action) => {
      state.resetFilter = action.payload
    },
    resetCustomersListItems: (state) => {
      state.customersListItems = [];
    },
    setSalesPersonListItems: (state, action) => {
      state.salesPersonListItems = action.payload
    },
    resetSalesPersonListItems: (state) => {
      state.salesPersonListItems = [];
    },
    setOrderTypeLisItems: (state, action) => {
      state.orderTypeListItems = action.payload
    },
    resetOrderTypeLisItems: (state) => {
      state.orderTypeListItems = [];
    },
    resetOrders: (state, action) => ordersAdapter.getInitialState({
      ...initState
    }),
  },
  extraReducers: {
    // PGSS-5448: last-dispatched wins, not last-to-respond. RTK stamps every dispatch with its own
    // meta.requestId; pending records which one is current, and a response whose id is no longer
    // the current one is discarded rather than painted over the newer results.
    [getOrders.pending]: (state, action) => {
      state.ordersRequestId = action.meta.requestId;
      ordersAdapter.removeAll(state);
    },
    [getOrders.fulfilled]: (state, action) => {
      if (state.ordersRequestId !== action.meta.requestId) return;
      ordersAdapter.setAll(state, action);
    },
    [getOrders.rejected]: (state, action) => {
      // Guarded too: a failed old request must not clear the spinner for a newer one still running.
      if (state.ordersRequestId !== action.meta.requestId) return;
      state.loading = false
    },
    [removeOrders.fulfilled]: (state, action) => ordersAdapter.removeMany(state, action.payload),
    [getOrderStatuses.fulfilled]: (state, action) => {
      if (action.payload.params && action.payload.params.on === 'y') {
        state.orderStatusesNames = action.payload.data;
      } else if (action.payload.params && action.payload.params.c === "order_type") {
        state.orderTypeNames = action.payload.data.map(function (a) {
          return a.name;
        });
      } else if (action.payload.params && action.payload.params.c === "container_shipment") { // stores the value of the type in the store 
        state.containerShipmentNames = action.payload.data.map(function (a) {
          return a.name;
        });
      }
       else if (action.payload.params && action.payload.params.c === "container_management_status") { // stores the value of the type in the store 
        state.loadStatusesNames = action.payload.data.map(function (a) {
          return a.name;
        });
      }else if (action.payload.params && action.payload.params.c === "shipment_type") {
        state.shipmentTypeNames = action.payload.data.map(function (a) {
          return a.name;
        });
      } else if (action.payload.params && action.payload.params.c === "payment_type") {
        state.paymentTypeNames = action.payload?.data?.map(function (a) {
          return a.name;
        });
      } else {
        let statuses = action.payload.data;
        if (action.payload.params?.scopeToWaitingApproval) {
          statuses = scopeCustomerWaitingApprovalStatuses(statuses, action.payload.params.c);
        } else if (action.payload.params?.excludeWaitingApproval) {
          statuses = excludeCustomerOrderWaitingApprovalStatuses(statuses, action.payload.params.c);
        }
        state.orderStatuses = statuses;
        state.orderStatusesNames = statuses.map(function (a) {
          return a.name;
        });
      }
    },
    [getUpdateStatusValues.fulfilled]: (state, action) => {
      state.updateStatusValues = action.payload;
    },
    [getImportOrders.fulfilled]: (state, action) => {
      state.importOrders = action.payload;
      state.loading = false
    },
    [getImportOrdersDetail.fulfilled]: (state, action) => {
      state.importDetails = action.payload;
      state.loading = false
    },
    [getSingleImportOrders.fulfilled]: (state, action) => {
      state.singleImportOrders = action.payload;
      state.loading = false
    },
    [getPriceBookImports.fulfilled]: (state, action) => {
      state.priceBookImports = action.payload;
      state.loading = false
    },
    [getSuppliers.fulfilled]: (state, action) => {
      state.suppliersList = action.payload;
      state.suppliersListLoading = false;
    },
    [getSuppliers.rejected]: (state, action) => {
      state.suppliersListLoading = false;
    },
    [getSuppliers.pending]: (state, action) => {
      state.suppliersList = [];
      state.previousSuppliers = [];
      state.suppliersListLoading = true;
    },
    [viewSupplierShipment.fulfilled]: (state, action) => {
      state.supplierShipmentValues = action.payload
    },
    [viewSupplierShipment.pending]: (state, action) => {
      state.supplierShipmentValues = {}
    },
    [getPaymentHistory.pending]: (state, action) => {
      state.paymentHistory = []
    },
    [getPaymentHistory.fulfilled]: (state, action) => {
      state.paymentHistory = action.payload
    },
    [getCustomersList.fulfilled]: (state, action) => {
      const { data } = action.payload;
      state.customersListItems = data;
    },
    [getStatuses.fulfilled]: (state, action) => {
      const { data, params } = action.payload;
      if (params && params === 'order_type') {
        state.orderTypeListItems = data;
        state.orderTypes = data;
      }
    },
    [getShipToLocations.fulfilled]: (state, action) => {
      state.shipToNames = action.payload.map(function (a) {
        return a.name;
      });
    },
    [getRequestedByList.fulfilled]: (state, action) => {
      state.requestedByNames = action.payload.map(function (a) {
        return a.name;
      });
    },
    [getContainerNumbersList.fulfilled]: (state, action) => {
      state.containerNumbers = action.payload;
    },
  },
});

export const {
  setLoading,
  resetLoading,
  setOrdersSearchText,
  setCustomer,
  setSalesPerson,
  setSelectedValue,
  setSupplier,
  openReallocationDialog,
  closeReallocationDialog,
  reAllocationFrom,
  reAllocationTo,
  selectedOrderDetails,
  orderForReallocation,
  setPreviousSuppliers,
  setOrderListHeaders,
  setParams,
  setResetFilters,
  setOrderTypes,
  resetOrders,
  setSalesPersonListItems,
} = ordersSlice.actions;

export default ordersSlice.reducer;